// Ingestion filters and grouping for Sentry (JURYBOX-WEB-APP-18, -19; the
// key-init family JURYBOX-WEB-APP-K, -X, -13, -1B, -1F, and -17 on the unwrap
// path; and the React DOM-mutation family JURYBOX-WEB-APP-T and -A).
//
// Browser extensions inject their scripts into the page and their failures
// surface through the very same global handlers the app installs, so an
// extension's bugs arrive in Jurybox's Sentry project looking like ours.
// Nothing in this codebase can fix a third party's extension, and the volume
// crowds real signal out of triage: a single PayPal Honey install produced
// three new issue groups in one page load.
//
// Two distinct shapes need two distinct filters, because they arrive with
// different amounts of evidence:
//
//   1. The extension's script IS the top stack frame — a file inside the
//      extension bundle. `denyUrls` matches on that frame, so
//      EXTENSION_URL_PATTERNS handles this case.
//   2. There is no stack frame at all. An extension dispatches a synthetic
//      `unhandledrejection` at `window`, and the SDK captures the DOM Event
//      object itself as the "rejection reason". `denyUrls` cannot see these —
//      there is no URL to match — so isDomEventRejection catches them by the
//      shape of the captured value instead.

import isUnreachableError from './isUnreachableError';

/**
 * URL fragments that identify a browser-extension script rather than app code.
 *
 * Covers the `*-extension://` schemes, plus the two on-disk layouts Safari
 * uses for extensions shipped inside a native app bundle
 * (`.safariextension/` resource directories and `Extension.appex/` App
 * Extension bundles) — the latter is what JURYBOX-WEB-APP-18 reports.
 *
 * On the scheme list: Chromium browsers (Chrome, Edge, Brave, Opera) all use
 * `chrome-extension://`, so that one entry covers Edge too.
 * `ms-browser-extension://` is legacy EdgeHTML only — kept because it costs
 * nothing, not because modern Edge needs it.
 */
export const EXTENSION_URL_PATTERNS = [
  /^(?:chrome|chrome-untrusted|moz|safari|safari-web|ms-browser)-extension:\/\//i,
  /\.safariextension\//i,
  /\/Extension\.appex\//i,
];

/**
 * True when the value a promise rejected with is a DOM Event.
 *
 * No first-party code here rejects with one: every `reject()` call site in
 * `src` (`fetchWithTimeout`, `asyncActions`, `WebSocketClient`, `Root.jsx`)
 * passes an Error or a caught exception, and Dexie/IndexedDB and Web Crypto
 * failures surface as `DOMException`, which is not an `Event`. What we see
 * instead is an extension re-dispatching a synthetic `unhandledrejection` at
 * the window, which the SDK's global handler captures verbatim.
 *
 * Scope worth being honest about: that enumeration covers `src`, not our
 * dependencies. A third-party library that rejected with a `ProgressEvent`
 * from a storage or IDB handler would be dropped here too. That is accepted
 * rather than overlooked — such a rejection carries no stack, no message and
 * no app frames, which is the same reason this filter exists at all — but it
 * is a wider net than "app code never does this" alone would justify.
 *
 * @param {{originalException?: unknown}} [hint] - Sentry's beforeSend hint
 * @returns {boolean} true when the event should be dropped
 */
export function isDomEventRejection(hint) {
  if (typeof Event === 'undefined') {
    return false;
  }
  return hint?.originalException instanceof Event;
}

/**
 * Stable grouping key for "the server could not be reached while unlocking
 * local data" — the failure behind JURYBOX-WEB-APP-K, -X, -13 and -1B.
 *
 * Those four issues are one failure. Sentry groups a thrown Error by the stack
 * frame that threw it, and this Error is constructed inside the `getKeyStatus`
 * catch in `authContext`, so every refactor that moves that `throw` opens a
 * brand-new issue group:
 *
 *   | Issue      | Culprit | Line | Events / users |
 *   |------------|---------|------|----------------|
 *   | WEB-APP-K  | `mc`    | 101  | 24 / 23        |
 *   | WEB-APP-X  | `nl`    |  --  |  3 / 3         |
 *   | WEB-APP-13 | `rl`    | 291  | 10 / 9         |
 *   | WEB-APP-1B | `pc`    | 337  |  1 / 1         |
 *
 * Together that is 38 events across ~36 users, the largest error class in this
 * app. Split four ways it reads as a one-event curiosity: an `is:unresolved`
 * triage query sees only the newest fragment, and because three of the four are
 * already adjudicated and closed, a recurrence lands in a *different* group than
 * the one that was resolved, so Sentry's regression detection never fires. The
 * splitting also hides its own scale: this family was triaged as three groups
 * totalling 16 events the day before WEB-APP-X was found.
 *
 * compliance-log#223 tracks the underlying transport failure, whose cause is
 * still unknown and which this does not attempt to fix. The fingerprint is that
 * issue's own first recommended next step: without it, the next refactor of this
 * path mints a fifth group and starts the count over again.
 *
 * `authContext`'s own docblock asserts that translating the message at the
 * display site "keeps the raw error intact for `isUnreachableError`, Sentry
 * grouping and the linked-error trail". That is true of the message, but not of
 * the grouping: #228 moved the throw site and WEB-APP-1B opened five weeks
 * later. An explicit fingerprint is what actually decouples the two.
 *
 * Deliberately narrow on both axes:
 *
 * - It requires the `app_init_stage: 'encryption-key'` tag, which this app sets
 *   on purpose at the single call site that reports init failures, rather than
 *   matching message text — that text varies with HTTP status and has already
 *   been rewritten once (#228).
 * - It requires `isUnreachableError`, so a request the server actually answered
 *   keeps its default grouping. A 500 from `/keys/unwrap` and a dropped
 *   connection need different triage, and merging them would trade one bad
 *   grouping for another.
 *
 * Wider than those four issues, though, and deliberately so: the gate is the
 * whole `encryption-key` stage, not the `getKeyStatus` catch. That stage has a
 * single init-failure capture site, and its own comment describes what reaches
 * it as "server unreachable, DEK unwrap/wrap failure" — so a transport timeout
 * propagated raw from `unlockDek`/`serverWrapDek` is tagged and matched here
 * too. JURYBOX-WEB-APP-17 (25 s on `/api/keys/unwrap`) is exactly that shape
 * and will join this group.
 *
 * That is the right grain rather than an accident. WEB-APP-17 and WEB-APP-13
 * are the *same* 47-second connectivity episode — same user, same trace
 * (`074fc007…`), 13 seconds apart — reaching the user through the unwrap path
 * and the status path respectively. "Could not reach the server during key
 * init" is one operational problem however it surfaces. Stated plainly so the
 * next reader is not surprised to find an unwrap timeout in a group the table
 * above does not list.
 *
 * No `'{{ default }}'` element, by decision rather than omission: appending
 * Sentry's default contribution would re-split this group by stack frame,
 * which is the precise behaviour the fingerprint exists to replace. Every
 * matching event therefore collapses into one group across releases and throw
 * sites.
 */
export const KEY_INIT_UNREACHABLE_FINGERPRINT = Object.freeze([
  'key-init',
  'server-unreachable',
]);

/**
 * Stable grouping key for "React's DOM mutation found the live DOM out of step
 * with its fiber tree" — the failure behind JURYBOX-WEB-APP-T and -A.
 *
 * Those two issues are one failure. They arrive through the same component
 * path, byte for byte, from `CasePage` down:
 *
 *   JurorDialog -> MUI Dialog -> Modal -> Portal -> FocusTrap -> Fade
 *     -> react-transition-group Transition -> Paper -> DialogContent
 *     -> Grid -> RichTextEditor -> draft-js DraftEditor
 *
 * and both land in react-dom's commit-mutation phase with `DOMException.code`
 * 8 (`NOT_FOUND_ERR`). They differ only in which mutation React was partway
 * through when it noticed:
 *
 *   | Issue     | Operation      | First seen | Events / users | Status   |
 *   |-----------|----------------|------------|----------------|----------|
 *   | WEB-APP-T | `insertBefore` | 2026-08-04 | 4 / 4          | open     |
 *   | WEB-APP-A | `removeChild`  | 2026-07-14 | 5 / 3          | archived |
 *
 * draft-js owns the contenteditable subtree, and the browser mutates its child
 * list directly, outside React's knowledge. A collaborator's fold-in then
 * rebuilds that list: `JurorDialog` hands a remote juror update to
 * `RichTextEditor.applyRemoteContent`, `reconcileRemoteContent` builds fresh
 * blocks from the merged text, and React re-renders the draft subtree. Lines
 * the remote added become new host nodes to place; lines it removed become
 * host nodes to delete. If the local user is typing when the fold lands, the
 * sibling React remembered has moved, and the call fails — as a placement
 * (-T) or as a deletion (-A), according to which way the fold went. The two
 * directions are one ownership conflict, not two bugs.
 *
 * compliance-log#219 reached that hypothesis from -T's replays, and recorded
 * that the MUI `Fade`/`Portal` unmount path is *not* the trigger: that wrapper
 * sits in the component stack on every render of this dialog, and no replay
 * shows the dialog closing at the moment of the crash. -A's replay
 * (`5ed3ad64`) is why the two are grouped here rather than merely filed as
 * lookalikes — it carries the same signature: a `Pending update added` and
 * `Juror updated locally` pair for juror `6abe94aa`, then a `Deferred remote
 * juror update (websocket-batch)` for that same juror two seconds later. Local
 * pending edits colliding with a remote fold on one record is exactly the
 * collision #219 describes, and -A is the first event to show it on the
 * deletion side.
 *
 * The split has the same cost here that it had for the key-init family, and
 * one more besides: **-A is archived and -T is open.** An `is:unresolved`
 * triage query sees only -T, so a recurrence that happens to land in -A is
 * invisible to the backlog sweep *and* suppressed by the archive. That is not
 * hypothetical — -A took an event on release `2026.10.3` on 2026-10-20, three
 * months after it was last seen, and surfaced only to a raw event-stream
 * query. Neither is Sentry's regression detection a backstop: a recurrence
 * lands in a different group than the one that was adjudicated, so it never
 * fires.
 *
 * compliance-log#219 tracks the underlying draft-js/React conflict, which this
 * does **not** attempt to fix, and is worth reading before anyone tries: one
 * structural remedy for -T has already shipped (web#210) and the crash
 * outlived it, and every remaining candidate trades a rare crash against
 * behaviour every collaborating user sees — deferring folds while the editor
 * has focus delays a collaborator's text, remounting the subtree on a fold
 * loses the local cursor that `applyRemoteContent` exists to preserve. That
 * call is the owner's, not a triage routine's, and #219's own next step is to
 * instrument the fold before changing it. All this change does is put the
 * family's events in one group, so its real size and its recurrences are
 * visible to the sweep that is supposed to find them. It touches `beforeSend`
 * only: no app behaviour moves, which is the whole reason it is safe to ship
 * while the fix itself waits for evidence.
 *
 * Three narrow gates, all of them runtime properties of the error object.
 * `beforeSend` runs in the browser, *before* Sentry applies sourcemaps, so
 * stack frames here carry minified bundle URLs rather than
 * `node_modules/react-dom/...` paths — "the frame that threw is inside
 * react-dom", the most direct statement of this failure, is simply not
 * observable at this point and is deliberately not attempted:
 *
 * - **React reported it.** `reactErrorHandler` appends an `Error` named
 *   `React ErrorBoundary <name>` to the error's `cause` chain whenever React
 *   hands it a component stack. Nothing else in this app produces that name,
 *   so it separates a crash React raised during render or commit from the
 *   same `DOMException` captured explicitly somewhere else. The chain is
 *   walked rather than checked one level deep because `setCause` appends to
 *   the end of whatever chain already exists.
 * - **It is a missing-node DOM failure.** `NotFoundError` with `code` 8,
 *   rather than message text, for the identity of the error itself.
 * - **The failed call was a Node mutation.** This is the one gate that reads
 *   the message, and it exists to keep IndexedDB out: a missing object store
 *   also raises `NotFoundError` with `code` 8, and a Dexie read that throws
 *   into render would clear the first two gates. This app has a live
 *   IndexedDB error class a stone's throw away (JURYBOX-WEB-APP-1E), so the
 *   two must not merge.
 *
 * Reading the message is a departure from `keyInitFingerprint`, which avoids
 * message text on purpose — but for a different reason than applies here.
 * There the text was ours, varied with HTTP status, and had already been
 * rewritten once (#228). These method names come from the DOM spec and are
 * emitted by the engine, not by us; `removeChild` is `removeChild` in every
 * release we will ever ship. The wording *around* them is the engine's own,
 * though, and Safari phrases this error without naming the method at all, so
 * the gate is **deliberately under-inclusive**: a Safari-worded event keeps
 * its default grouping. That is the safe direction to miss in. Failing to
 * merge leaves today's behaviour in place; merging too eagerly would put an
 * IndexedDB fault in a group named for a React one, which is the trade
 * `keyInitFingerprint` declined and this declines too.
 *
 * No `'{{ default }}'` element, for the same reason as the key-init
 * fingerprint: appending Sentry's default contribution would re-split the
 * group by stack frame, which is the behaviour being replaced.
 */
export const REACT_DOM_MUTATION_FINGERPRINT = Object.freeze([
  'react-dom-mutation',
  'node-not-found',
]);

/**
 * `Node` mutation methods whose failure means React and the live DOM disagree
 * about where a node is. Matched against the engine's message, which names the
 * method that was called.
 */
const NODE_MUTATION_METHODS = Object.freeze([
  'insertBefore',
  'removeChild',
  'appendChild',
  'replaceChild',
]);

// A chain long enough to cover any real `cause` nesting, short enough that a
// cycle `setCause`'s own WeakSet guard somehow let through cannot spin here.
const MAX_CAUSE_DEPTH = 10;

/**
 * True when React handed this error to Sentry with a component stack.
 *
 * `@sentry/react`'s `captureReactException` builds an `Error` named
 * `React ErrorBoundary <original name>`, stacks the component stack on it and
 * appends it to the error's `cause` chain. That name is the marker; it is
 * added for both of React 19's root callbacks (`onCaughtError` and
 * `onUncaughtError`), so this does not distinguish a crash a boundary caught
 * from one that reached the root, and does not need to.
 *
 * @param {unknown} error - the original exception
 * @returns {boolean} true when React reported it
 */
export function isReactReportedError(error) {
  let current = error;
  for (let depth = 0; current && depth < MAX_CAUSE_DEPTH; depth += 1) {
    if (typeof current.name === 'string'
      && current.name.startsWith('React ErrorBoundary')) {
      return true;
    }
    current = current.cause;
  }
  return false;
}

/**
 * True when a `Node` mutation failed because the node was not where the caller
 * expected it.
 *
 * `code` 8 is `DOMException.NOT_FOUND_ERR`; it is checked alongside the name
 * so a plain `Error` that merely calls itself `NotFoundError` does not match.
 *
 * @param {unknown} error - the original exception
 * @returns {boolean} true for a missing-node DOM mutation failure
 */
export function isNodeNotFoundError(error) {
  if (error?.name !== 'NotFoundError' || error?.code !== 8) {
    return false;
  }
  const message = typeof error.message === 'string' ? error.message : '';
  return NODE_MUTATION_METHODS.some((method) => message.includes(method));
}

/**
 * The React DOM-mutation fingerprint for an event, or null to leave Sentry's
 * grouping alone.
 *
 * @param {object} event - the Sentry event
 * @param {object} [hint] - the accompanying hint, carrying originalException
 * @returns {string[]|null} the fingerprint to apply, or null
 */
export function reactDomMutationFingerprint(event, hint) {
  const error = hint?.originalException;
  if (!isReactReportedError(error)) {
    return null;
  }
  if (!isNodeNotFoundError(error)) {
    return null;
  }
  return [...REACT_DOM_MUTATION_FINGERPRINT];
}

/**
 * The fingerprint for an event, or null to leave Sentry's grouping alone.
 *
 * @param {object} event - the Sentry event
 * @param {object} [hint] - the accompanying hint, carrying originalException
 * @returns {string[]|null} the fingerprint to apply, or null
 */
export function keyInitFingerprint(event, hint) {
  if (event?.tags?.app_init_stage !== 'encryption-key') {
    return null;
  }
  if (!isUnreachableError(hint?.originalException)) {
    return null;
  }
  return [...KEY_INIT_UNREACHABLE_FINGERPRINT];
}

/**
 * Sentry `beforeSend` hook: drop known-unactionable third-party noise, and hold
 * two recurring failures each in a single issue group across refactors.
 *
 * The two fingerprints are mutually exclusive in practice — one gates on the
 * `encryption-key` init stage and an unreachable server, the other on a
 * React-reported DOM mutation — so the order they are tried in carries no
 * meaning beyond giving the key-init rule, the narrower of the two, the first
 * say.
 *
 * Kept deliberately narrow — it only discards events this app provably cannot
 * produce. Anything ambiguous is left to reach Sentry, where it can be triaged
 * against real evidence rather than silently dropped at the client.
 *
 * @param {object} event - the Sentry event
 * @param {object} [hint] - the accompanying hint, carrying originalException
 * @returns {object|null} the event to send, or null to drop it
 */
export function beforeSend(event, hint) {
  if (isDomEventRejection(hint)) {
    return null;
  }
  const fingerprint = keyInitFingerprint(event, hint)
    ?? reactDomMutationFingerprint(event, hint);
  if (fingerprint) {
    event.fingerprint = fingerprint;
  }
  return event;
}
